import React, { useEffect, useState } from 'react';
import { OrderItem, Vendor, SenderInfo, RecipientInfo } from '../types';
import { formatNaira } from '../lib/data';
import { API_ENDPOINTS, API_CONFIG } from '../lib/config';
import useSEO from '../hooks/useSEO';
import { fetchFees } from '../hooks/useProductCatalog';
import { Button, FlowPage, Icon, Thumb } from '../components/bx';
import { flowSteps } from './ItemsPage';
import '../styles/bx.css';

interface PaymentPageProps {
  isGetMe?: boolean;
  items: OrderItem[];
  vendor: Vendor;
  sender: SenderInfo;
  recipient: RecipientInfo;
  onSubmit: () => void;
  onBack: () => void;
}

interface OrderPayload {
  sender: { name: string; email_address: string; phone_number: string };
  type: 'purchase' | 'gift';
  recipient: { name: string; email_address: string; phone_number: string; delivery_address: string };
  items: Array<{ item: string; item_name: string; quantity: number }>;
  message: string;
  vendor: string;
  totalAmount: number;
  callback_url?: string;
}

export default function PaymentPage({ isGetMe, items, vendor, sender, recipient, onBack }: PaymentPageProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fees, setFees] = useState<{ fees: number; total: number } | null>(null);

  useSEO({ title: 'Review & pay - BOQBOX', description: 'Review your order and pay securely with Paystack.' });

  const subtotal = items.reduce((sum, oi) => sum + (oi.item.price ?? 0) * oi.quantity, 0);
  const address = [recipient.address, recipient.city, recipient.state].filter(Boolean).join(', ');

  // The server adds service + payment fees; show the exact amount Paystack will charge.
  useEffect(() => {
    let live = true;
    fetchFees(subtotal).then((data) => live && setFees(data));
    return () => { live = false; };
  }, [subtotal]);

  const constructOrderPayload = (): OrderPayload => ({
    sender: { name: sender.fullName, email_address: sender.email, phone_number: sender.phone },
    type: isGetMe ? 'purchase' : 'gift',
    recipient: { name: recipient.fullName, email_address: recipient.email, phone_number: recipient.phone, delivery_address: address },
    items: items.map((oi) => ({ item: oi.item._id || oi.item.id || '', item_name: oi.item.name, quantity: oi.quantity })),
    message: recipient.message || '',
    vendor: vendor.name,
    totalAmount: subtotal,
    callback_url: API_CONFIG.CALLBACK_URL,
  });

  const handlePlaceOrder = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.ORDER_CREATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(constructOrderPayload()),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success || !data.payment_url) {
        throw new Error(data?.message || 'We couldn’t start your payment. Please try again.');
      }
      window.location.href = data.payment_url;
    } catch (err) {
      setIsLoading(false);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const total = fees?.total ?? subtotal;
  const payLabel = isLoading ? 'Opening Paystack…' : `Pay ${formatNaira(total)}`;

  const totals = (
    <div className="bx-sum">
      <div className="bx-sum-row"><span>Subtotal</span><strong>{formatNaira(subtotal)}</strong></div>
      <div className="bx-sum-row"><span>Delivery</span><strong>Free</strong></div>
      <div className="bx-sum-row"><span>Service & payment fees</span><strong>{fees ? formatNaira(fees.fees) : '…'}</strong></div>
      <div className="bx-sum-total"><span>Total</span><span>{formatNaira(total)}</span></div>
    </div>
  );

  return (
    <FlowPage
      eyebrow={isGetMe ? 'Get me' : 'Send a gift'}
      title="Review & pay"
      subtitle="Check everything looks right. You’ll pay securely on Paystack."
      steps={flowSteps(isGetMe)}
      step={4}
      onBack={onBack}
      barMobileOnly
      aside={
        <div className="bx-card bx-card-pad bx-stack">
          <h2>Order total</h2>
          {totals}
          {error ? <div className="bx-alert">{error}</div> : null}
          <Button block size="lg" onClick={handlePlaceOrder} loading={isLoading} disabled={!fees && !error}>
            <Icon name="lock" /> {payLabel}
          </Button>
          <span className="bx-faint" style={{ fontSize: 12.5, textAlign: 'center' }}>Card, bank transfer or USSD via Paystack</span>
        </div>
      }
      bar={
        <>
          <div className="bx-bar-copy"><small>Total</small><strong>{formatNaira(total)}</strong></div>
          <Button onClick={handlePlaceOrder} loading={isLoading} disabled={!fees && !error}><Icon name="lock" /> {isLoading ? 'Opening…' : 'Pay now'}</Button>
        </>
      }
    >
      <div className="bx-stack">
        <div className="bx-card bx-card-pad">
          <div className="bx-between" style={{ marginBottom: 6 }}>
            <h2>{isGetMe ? 'Your items' : 'The gift'}</h2>
            <span className="bx-pill">{items.reduce((n, oi) => n + oi.quantity, 0)} items</span>
          </div>
          {items.map((oi) => (
            <div key={oi.item._id || oi.item.id} className="bx-cart-line">
              <Thumb src={oi.item.image_url} category={oi.item.category} />
              <div style={{ minWidth: 0 }}>
                <strong>{oi.item.name}</strong>
                <small>{oi.quantity} × {formatNaira(oi.item.price ?? 0)}</small>
              </div>
              <strong>{formatNaira((oi.item.price ?? 0) * oi.quantity)}</strong>
            </div>
          ))}
        </div>

        <div className="bx-card bx-card-pad">
          <h2>Delivery</h2>
          <div className="bx-sum" style={{ marginTop: 12 }}>
            <div className="bx-sum-row"><span>{isGetMe ? 'Deliver to' : 'Recipient'}</span><strong>{recipient.fullName}{recipient.phone ? ` · ${recipient.phone}` : ''}</strong></div>
            <div className="bx-sum-row"><span>Address</span><strong>{address}</strong></div>
            <div className="bx-sum-row"><span>Vendor</span><strong>{vendor.name}</strong></div>
            {!isGetMe ? <div className="bx-sum-row"><span>From</span><strong>{sender.fullName}</strong></div> : null}
            {!isGetMe && recipient.message ? <div className="bx-sum-row"><span>Gift note</span><strong style={{ fontWeight: 500, fontStyle: 'italic' }}>“{recipient.message}”</strong></div> : null}
          </div>
          <Button variant="ghost" size="sm" style={{ marginTop: 10, paddingLeft: 0 }} onClick={onBack}><Icon name="edit" size={15} /> Edit delivery details</Button>
        </div>

        <div className="bx-card bx-card-pad bx-mobile-total">
          <h2 style={{ marginBottom: 12 }}>Order total</h2>
          {totals}
          {error ? <div className="bx-alert" style={{ marginTop: 12 }}>{error}</div> : null}
        </div>

        <div className="bx-alert info"><Icon name="shield" /> <span>Delivered in a plain, unlabelled bag. Your payment is processed securely by Paystack — BOQBOX never sees your card.</span></div>
      </div>
    </FlowPage>
  );
}
